import React from 'react';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { City } from '@/data/cities';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import { cityLanguageFlags, cityLanguageNames } from '@/utils/cityLanguages';
import { cn } from '@/lib/utils';

interface CityLanguageLinksProps {
  city: City;
  currentLanguage: 'en' | string;
  compact?: boolean; // Modo compacto para header
}

export const CityLanguageLinks: React.FC<CityLanguageLinksProps> = ({ 
  city, 
  currentLanguage,
  compact = false 
}) => {
  const localLanguage = detectCityLanguage(city);
  
  // Only show if city has a local language different from English
  if (localLanguage === 'en') {
    return null;
  }

  const links = [
    {
      language: 'en',
      url: `/city/${city.slug}`,
      flag: cityLanguageFlags['en'],
      name: cityLanguageNames['en']
    },
    {
      language: localLanguage,
      url: `/${localLanguage}/city/${city.slug}`,
      flag: cityLanguageFlags[localLanguage as keyof typeof cityLanguageFlags],
      name: cityLanguageNames[localLanguage as keyof typeof cityLanguageNames]
    }
  ];

  if (compact) {
    // Versión compacta para el header (con banderas bien visibles)
    // NOTE: No usar React.Fragment aquí porque el editor de Lovable instrumenta
    // los elementos con data-lov-id, lo que causa errores en Fragments
    return (
      <nav className="flex items-center gap-1.5 px-2.5 py-1 bg-background rounded-full border border-border shadow-sm">
        <Globe className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        <div className="flex items-center gap-1.5">
          {links.map((link, index) => (
            <span key={link.language} className="inline-flex items-center">
              {index > 0 && <span className="mx-1 text-muted-foreground/60" aria-hidden="true">|</span>}
              <Link
                to={link.url}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-1 transition-colors",
                  currentLanguage === link.language
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted text-foreground"
                )}
                aria-label={`View this page in ${link.name}`}
                hrefLang={link.language}
              >
                <span className="text-lg leading-none">{link.flag}</span>
                <span className="text-xs font-medium tracking-wide">
                  {link.language === 'en' ? 'EN' : link.name.slice(0,2).toUpperCase()}
                </span>
              </Link>
            </span>
          ))}
        </div>
      </nav>
    );
  }

  // Versión original para cuerpo de la página
  return (
    <div className="flex items-center gap-3 text-sm mb-4">
      <span className="text-muted-foreground">View in:</span>
      {links.map((link) => (
        <Link
          key={link.language}
          to={link.url}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors",
            currentLanguage === link.language 
              ? "bg-primary text-primary-foreground font-medium" 
              : "bg-muted hover:bg-muted/80 text-foreground"
          )}
          aria-label={`View this page in ${link.name}`}
          hrefLang={link.language}
        >
          <span className="text-base leading-none">{link.flag}</span>
          <span>{link.name}</span>
        </Link>
      ))}
    </div>
  );
};
